import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ROLE_LABELS, STATUS_OPTIONS } from "@/lib/constants";
import { Search, Plus, Pencil, UserX, UserCog } from "lucide-react";

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  status: "active",
  branch_id: "",
  branch_ids: [],
  role_id: "",
  password: "",
};

export default function Usuarios() {
  const { isSuperAdmin, activeTenantId, authorizedBranches: branches } = useTenant();
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);
  const roleMap = useMemo(() => Object.fromEntries(roles.map((r) => [r.id, r])), [roles]);

  async function fetchAll() {
    if (!canQuery) {
      setItems([]);
      setRoles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [{ data: u }, { data: r }] = await Promise.all([
        api.get("/users", { params: { ...tenantParam, search: search || undefined } }),
        api.get("/roles", { params: tenantParam }),
      ]);
      setItems(u);
      setRoles(r.filter((role) => role.name !== "super_admin"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeTenantId, isSuperAdmin]);

  function openCreate() {
    setEditing(null);
    const main = branches.find((b) => b.is_main) || branches[0];
    const defaultRole = roles.find((r) => r.name === "recepcion") || roles[0];
    setForm({ ...EMPTY, branch_id: main?.id || "", role_id: defaultRole?.id || "" });
    setSheetOpen(true);
  }
  function openEdit(u) {
    setEditing(u);
    setForm({
      ...EMPTY,
      full_name: u.full_name,
      email: u.email,
      phone: u.phone || "",
      status: u.status,
      branch_id: u.branch_id || "",
      branch_ids: u.branch_ids || [],
      role_id: u.role_id,
      password: "",
    });
    setSheetOpen(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { password, ...payload } = form;
        await api.patch(`/users/${editing.id}`, payload);
        if (password) {
          await api.post(`/users/${editing.id}/reset-password`, { new_password: password });
        }
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", form, { params: tenantParam });
        toast.success("Usuario creado");
      }
      setSheetOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function doDeactivate() {
    if (!confirmDeactivate) return;
    try {
      await api.delete(`/users/${confirmDeactivate.id}`);
      toast.success("Usuario desactivado");
      setConfirmDeactivate(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="page-usuarios">
      <PageHeader
        title="Usuarios"
        description="Los usuarios se asignan a una sucursal y a un rol. Los roles definen los permisos."
        actions={
          <Button
            className="btn-tenant"
            onClick={openCreate}
            disabled={!canQuery || branches.length === 0 || roles.length === 0}
            data-testid="btn-new-usuario"
          >
            <Plus className="h-4 w-4 mr-2" /> Nuevo usuario
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o correo…"
            className="pl-9 h-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-usuarios"
          />
        </div>
        <div className="text-xs text-muted-foreground ml-auto" data-testid="usuarios-count">
          {items.length} usuario{items.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? "Aún no hay usuarios" : "Selecciona un tenant"}
            description="Crea el primer usuario para dar acceso a tu equipo."
            icon={UserCog}
            action={
              canQuery && (
                <Button className="btn-tenant" onClick={openCreate} data-testid="empty-new-usuario">
                  <Plus className="h-4 w-4 mr-2" /> Crear usuario
                </Button>
              )
            }
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Sucursal</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.id} className="tbl-row" data-testid={`row-usuario-${u.id}`}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-tenant text-tenant">
                      {ROLE_LABELS[u.role_name] || u.role_name}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{branchMap[u.branch_id]?.name || "—"}</TableCell>
                  <TableCell>
                    <Badge className={u.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                      {u.status === "active" ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`btn-edit-usuario-${u.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {u.id !== session?.user?.id && u.status === "active" && (
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDeactivate(u)} data-testid={`btn-deactivate-usuario-${u.id}`}>
                        <UserX className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sheet-usuario">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</SheetTitle>
            <SheetDescription>Asignación obligatoria de sucursal y rol.</SheetDescription>
          </SheetHeader>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo *</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="usuario-full-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="usuario-email" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="usuario-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rol *</Label>
                <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })} required>
                  <SelectTrigger data-testid="usuario-role"><SelectValue placeholder="Selecciona rol" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {ROLE_LABELS[r.name] || r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sucursal principal *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })} required>
                  <SelectTrigger data-testid="usuario-branch"><SelectValue placeholder="Selecciona sucursal" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2" data-testid="usuario-branch-ids">
              <Label>Sucursales autorizadas</Label>
              <div className="flex flex-wrap gap-3">
                {branches.map((b) => {
                  const checked = b.id === form.branch_id || form.branch_ids.includes(b.id);
                  return (
                    <label key={b.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={b.id === form.branch_id}
                        onChange={(e) => setForm({
                          ...form,
                          branch_ids: e.target.checked
                            ? [...form.branch_ids, b.id]
                            : form.branch_ids.filter((x) => x !== b.id),
                        })}
                        data-testid={`usuario-branch-ids-${b.id}`}
                      />
                      {b.name}
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">La sucursal principal siempre está autorizada.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estatus</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="usuario-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{editing ? "Nueva contraseña (opcional)" : "Contraseña *"}</Label>
                <Input
                  type="password"
                  required={!editing}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? "Dejar en blanco para no cambiar" : "Mínimo 6 caracteres"}
                  data-testid="usuario-password"
                />
              </div>
            </div>
            <SheetFooter>
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)} data-testid="usuario-cancel">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="btn-tenant" data-testid="usuario-save">
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear usuario"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmDeactivate} onOpenChange={(o) => !o && setConfirmDeactivate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDeactivate?.full_name}</b> ya no podrá iniciar sesión. Podrás reactivarlo después editándolo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDeactivate} className="bg-red-600 hover:bg-red-700" data-testid="confirm-deactivate-usuario">
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
